import React from 'react';
import AddIcon from '@mui/icons-material/AddBox';
import CollapseAll from '@mui/icons-material/UnfoldLess';
import {
  Alert,
  Box,
  Button,
  Divider,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
} from '@mui/material';

import { TooltipIconButton } from '@paragon/client-tools';
import { PROJECT_STATUSES, ProjectStatus } from '@paragon/contract-library';
import { changesExist, createProject } from '@paragon/layout-models';
import { getLoggedInUser, getOrganizationMembers, getUserDisplayName } from '@paragon/login-tools';

import { collapseAllRows } from '../store/actions/grid';
import { assigneeChanged, filtersCleared, statusChanged, UNASSIGNED } from '../store/slices/projectFiltersSlice';
import { useAppDispatch, useAppSelector } from './hooks';
import { ProjectsDateFilterPicker } from './ProjectsDateFilterPicker';
import { ProjectsSearchField } from './ProjectsSearchField';
import { SaveProjectButton } from './Tools/SaveProjectButton';

export function ProjectsToolbar() {
  const dispatch = useAppDispatch();
  const status = useAppSelector((state) => state.projectFilters.status);
  const assignee = useAppSelector((state) => state.projectFilters.assignee);
  const loggedInUser = useAppSelector(getLoggedInUser);
  const organizationMembers = useAppSelector(getOrganizationMembers);
  const filtersActive = useAppSelector(
    (state) =>
      state.projectFilters.search !== '' ||
      state.projectFilters.createdMonth !== null ||
      state.projectFilters.status !== null ||
      state.projectFilters.assignee !== null,
  );
  const projectChangesExist = useAppSelector(changesExist);

  // The logged-in user is offered as "Assigned to me", so listing them again would duplicate that option's value
  const otherMembers = organizationMembers.filter((member) => member.email !== loggedInUser?.email);

  const createAndOpenProject = async () => {
    await dispatch(createProject({ withSpinnerMessage: 'Loading project...' }));
  };

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1,

        padding: 1,
        marginY: 1,
      }}
    >
      <TooltipIconButton title="Collapse all" size="small" onClick={() => dispatch(collapseAllRows())}>
        <CollapseAll />
      </TooltipIconButton>

      <ProjectsDateFilterPicker />

      <ProjectsSearchField />

      <FormControl size="small" sx={{ minWidth: 130 }}>
        <InputLabel id="project-status-filter-label">Status</InputLabel>
        <Select
          labelId="project-status-filter-label"
          label="Status"
          value={status ?? ''}
          onChange={(event) => {
            const selectedStatus = event.target.value as ProjectStatus | '';
            dispatch(statusChanged(selectedStatus === '' ? null : selectedStatus));
          }}
        >
          <MenuItem value="">All statuses</MenuItem>
          {PROJECT_STATUSES.map((status) => (
            <MenuItem key={status} value={status}>
              {status}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl size="small" sx={{ minWidth: 150 }}>
        <InputLabel id="project-assignee-filter-label">Assignee</InputLabel>
        <Select
          labelId="project-assignee-filter-label"
          label="Assignee"
          value={assignee ?? ''}
          onChange={(event) => dispatch(assigneeChanged(event.target.value === '' ? null : event.target.value))}
        >
          <MenuItem value="">Anyone</MenuItem>
          {loggedInUser && <MenuItem value={loggedInUser.email}>Assigned to me</MenuItem>}
          <MenuItem value={UNASSIGNED}>Unassigned</MenuItem>
          {otherMembers.length > 0 && <Divider />}
          {otherMembers.map((member) => (
            <MenuItem key={member.email} value={member.email}>
              <ListItemText
                primary={getUserDisplayName(member)}
                slotProps={{ primary: { noWrap: true, variant: 'body2' } }}
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {filtersActive && (
        <Button size="small" color="inherit" onClick={() => dispatch(filtersCleared())}>
          Clear filters
        </Button>
      )}

      <Box sx={{ ml: 'auto', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        {projectChangesExist && (
          <Alert variant="outlined" severity="warning" sx={{ py: 0 }}>
            Some projects have unsaved changes.
          </Alert>
        )}
        <SaveProjectButton />

        <Button variant="contained" startIcon={<AddIcon />} onClick={() => void createAndOpenProject()}>
          New Project
        </Button>
      </Box>
    </Paper>
  );
}
